import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';

import rightArrow1 from '../../assets/rightLast.svg';
import rightArrow2 from '../../assets/right.svg';
import leftArrow1 from '../../assets/leftLast.svg';
import leftArrow2 from '../../assets/left.svg';
import iconDelete from '../../assets/icon_delete.svg';
import iconEditAdmin from '../../assets/edit_admin.svg';
import { useNavigate } from 'react-router-dom';
import AppSpinner from '../../components/AppSpinner';

interface BigFiveResultData {
  _id: string;
  user: {
    _id: string;
    displayName: string;
    userId: string;
    email: string;
  } | null;
  results: {
    [key: string]: number;
  };
  createdAt: string;
}

const AdminBigFiveResultListPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [results, setResults] = useState<BigFiveResultData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [keyword, setKeyword] = useState<string>('');

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const itemsPerPage = 20;

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const fetchResults = async (page: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin/big-five-results', {
        params: {
          page,
          limit: itemsPerPage,
          keyword,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setResults(response.data.results);
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.totalResults);
      setCurrentPage(response.data.currentPage);
    } catch (err) {
      setError('Big Five 성격검사 결과 목록을 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchResults(1);
  }, [isAuthenticated]);

  const handleSearch = () => {
    fetchResults(1);
    setSelectedIds([]);
  };

  const onReset = () => {
    setKeyword('');
    const token = localStorage.getItem('token');
    axios
      .get('/api/admin/big-five-results', {
        params: {
          page: 1,
          limit: itemsPerPage,
          keyword: '',
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(response => {
        setResults(response.data.results);
        setTotalPages(response.data.totalPages);
        setTotalCount(response.data.totalResults);
        setCurrentPage(1);
        setSelectedIds([]);
      });
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchResults(page);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = results.map(r => r._id);
      setSelectedIds(prev => Array.from(new Set([...prev, ...allIds])));
    } else {
      const pageIds = results.map(r => r._id);
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]));
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      alert('삭제할 항목을 선택해주세요.');
      return;
    }

    if (!window.confirm(`선택한 ${selectedIds.length}개의 결과를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await Promise.all(
        selectedIds.map(id =>
          axios.delete(`/api/admin/big-five-results/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      alert('삭제되었습니다.');
      fetchResults(currentPage);
      setSelectedIds([]);
    } catch (err) {
      alert('삭제 중 오류가 발생했습니다.');
      console.error(err);
    }
  };

  const formatResults = (res: { [key: string]: number }) => {
    const traits: { [key: string]: string } = {
      O: '개방성',
      C: '성실성',
      E: '외향성',
      A: '우호성',
      N: '신경증',
    };

    return Object.entries(res)
      .map(([key, value]) => {
        return `${traits[key] || key}: ${value}`;
      })
      .join(', ');
  };

  if (loading && results.length === 0) return <AppSpinner fullscreen tip="Loading..." />;

  return (
    <div className="admin-page">
      <h1>Big Five 성격검사 결과 관리</h1>

      <div className="search-filter-container">
        <div className="filter-group">
          <label>사용자 검색</label>
          <div className="search-input-group">
            <input
              type="text"
              placeholder="이름 또는 아이디를 입력하세요"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>

        <div className="button-group">
          <button className="btn-search" onClick={handleSearch}>
            검색
          </button>
          <button className="btn-reset" onClick={onReset}>
            초기화
          </button>
        </div>
      </div>

      <div className="list-info">
        총 <span className="count">{totalCount}</span>건 | 페이지{' '}
        <span className="page">
          {currentPage} / {totalPages || 1}
        </span>
      </div>


      <div className="table-scroll">
        <table className="table big-five-result-list-table">
          <colgroup>
            <col width="50px" />
            <col width="6%" />
            <col width="20%" />
            <col width="40%" />
            <col width="15%" />
            <col width="10%" />
          </colgroup>
          <thead>
            <tr>
              <th>
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={results.length > 0 && results.every(r => selectedIds.includes(r._id))}
                    onChange={handleSelectAll}
                  />
                  <span className="checkmark"></span>
                </label>
              </th>
              <th>No</th>
              <th>사용자 (아이디)</th>
              <th>검사 결과 (요인별 점수)</th>
              <th>검사 일시</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {results.length > 0 ? (
              results.map((result, index) => (
                <tr key={result._id}>
                  <td>
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(result._id)}
                        onChange={() => handleSelectOne(result._id)}
                      />
                      <span className="checkmark"></span>
                    </label>
                  </td>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>
                    {result.user ? (
                      <>
                        {result.user.displayName} ({result.user.userId || '소셜로그인'})
                      </>
                    ) : (
                      <span style={{ color: '#999' }}>(알 수 없음)</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'left', paddingLeft: '15px' }}>
                    {formatResults(result.results)}
                  </td>
                  <td>
                    {new Date(result.createdAt).toLocaleDateString()}{' '}
                    {new Date(result.createdAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td>
                    <button className="btn-icon view m-auto"  onClick={() => navigate(`/admin/big-five-results/${result._id}`)} title="상세보기"></button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="no-data">
                  데이터가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
            <img src={leftArrow1} alt="화살표" />
          </button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            <img src={leftArrow2} alt="화살표" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p >= currentPage - 2 && p <= currentPage + 2)
            .map(page => (
              <button
                key={page}
                className={currentPage === page ? 'active' : ''}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            <img src={rightArrow2} alt="화살표" />
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            <img src={rightArrow1} alt="화살표" />
          </button>
        </div>
        <div className="bottom-actions">
          <button className="btn-delete-selection" onClick={handleDeleteSelected}>
            <img src={iconDelete} alt="삭제" />
            삭제
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminBigFiveResultListPage;
